import { observer } from "mobx-react";
import { FC, useEffect, useMemo, useRef, useState } from "react";

import { useChatStore } from "../../../hooks/useChatStore";
import { useAppStore } from "../../../hooks/useAppStore";
import { useChatCategoryStore } from "../../../hooks/useChatCategoryStore";
import SidePanelContent, { PanelType } from "../../ui/SidePanelContent";
import ClaimPreview from "../../claims/ClaimPreview";
import { fetchFileContentById } from "../../../services";
import { isEdiFile } from "../../../services/fileParser";
import { saveAppealLetter } from "../../../services/fileDownloadService";
import type { AppealLetterFormat } from "../../../services/apiService";
import useNotifier from "../../../hooks/useNotifier";

interface ChatPanelProps {
  panelPosition: "left" | "right";
  setPanelPosition: (position: "left" | "right") => void;
}

/**
 * Defer revoke until after React has committed (avoids ERR_FILE_NOT_FOUND if an iframe
 * still had the blob URL, and avoids flushSync inside useEffect which React disallows).
 */
function scheduleRevokeBlobObjectUrl(url: string | null | undefined): void {
  if (!url?.startsWith("blob:")) return;
  const u = url;
  setTimeout(() => {
    if (typeof URL.revokeObjectURL === "function") {
      URL.revokeObjectURL(u);
    }
  }, 0);
}

const ChatPanel: FC<ChatPanelProps> = observer(
  ({ panelPosition, setPanelPosition }) => {
    const messageEditorRef = useRef<any>(null);

    const chatStore = useChatStore();
    const notifier = useNotifier();
    const { policyToPreview, contractToPreview } = useAppStore();
    const { isPoliciesDrawerOpen, isContractsDrawerOpen } =
      useChatCategoryStore();

    const [isLoadingFile, setIsLoadingFile] = useState(false);
    const [activeFileRawContent, setActiveFileRawContent] = useState<
      | {
          fileContent: string;
          contentType: string;
          rawContent: string;
        }
      | undefined
    >(undefined);
    const activePreviewBlobUrlRef = useRef<string | null>(null);

    useEffect(() => {
      if (!chatStore.fileToPreview) {
        const toRevoke = activePreviewBlobUrlRef.current;
        activePreviewBlobUrlRef.current = null;
        setActiveFileRawContent(undefined);
        setIsLoadingFile(false);
        scheduleRevokeBlobObjectUrl(toRevoke);
        return;
      }

      const prevBlob = activePreviewBlobUrlRef.current;
      activePreviewBlobUrlRef.current = null;
      setActiveFileRawContent(undefined);
      setIsLoadingFile(true);
      scheduleRevokeBlobObjectUrl(prevBlob);

      let cancelled = false;

      const previewFileName =
        chatStore.fileToPreview?.name ||
        chatStore.fileToPreview?.original_name ||
        chatStore.fileToPreview?.file_name ||
        "";

      // Must be the *same* name DocumentViewer renders by (REV-2354). This
      // read `file_name` alone, which an optimistically-added attachment does
      // not carry — askQuestion builds those as `{id, name, size, url}`. So a
      // file clicked on the message the user just sent was fetched raw while
      // DocumentViewer, going by `name`, still handed it to EdiClaimViewer:
      // "Unable to parse EDI data. Please check the format." on an EDI file
      // whose conversion had in fact succeeded.
      const isEdi = isEdiFile(previewFileName);
      const fileId = chatStore.fileToPreview.id;
      const fetchPreview = async () => {
        const res = await fetchFileContentById(
          fileId,
          true,
          isEdi,
          previewFileName,
        );
        // 404 on parsed content means this EDI file has no JSON yet (still
        // ingesting, or the conversion failed). Fall back to the raw file so
        // Raw has something real to show — otherwise the empty result falls
        // through to `file_url` in the viewer props and EdiClaimViewer reports
        // an unparseable S3 URL as a malformed EDI file.
        if (isEdi && !res.success && res.status === 404) {
          return fetchFileContentById(fileId, true, false, previewFileName);
        }
        return res;
      };

      fetchPreview()
        .then((res) => {
          const content = res.content || "";
          if (cancelled) {
            scheduleRevokeBlobObjectUrl(
              content.startsWith("blob:") ? content : null,
            );
            return;
          }
          setActiveFileRawContent({
            fileContent: content,
            contentType: res.contentType || "",
            rawContent: content,
          });
          activePreviewBlobUrlRef.current = content.startsWith("blob:")
            ? content
            : null;
          setIsLoadingFile(false);
        })
        .catch((err) => {
          console.error(err);
          if (!cancelled) {
            setIsLoadingFile(false);
            setActiveFileRawContent({
              fileContent: "",
              contentType: "",
              rawContent: "",
            });
          }
        });

      return () => {
        cancelled = true;
        const toRevoke = activePreviewBlobUrlRef.current;
        activePreviewBlobUrlRef.current = null;
        setActiveFileRawContent(undefined);
        scheduleRevokeBlobObjectUrl(toRevoke);
      };
    }, [chatStore.fileToPreview]);

    const activePanel = useMemo((): PanelType => {
      if (chatStore.letterToPreview) return "editor";
      if (chatStore.fileToPreview) return "viewer";
      if (isPoliciesDrawerOpen || policyToPreview) return "policy";
      if (isContractsDrawerOpen || contractToPreview) return "contract";
      return null;
    }, [
      chatStore.letterToPreview,
      chatStore.fileToPreview,
      isPoliciesDrawerOpen,
      policyToPreview,
      isContractsDrawerOpen,
      contractToPreview,
    ]);

    const handleClosePanel = async () => {
      await chatStore.saveLetterEdit();

      chatStore.setFileToPreview(null);
      chatStore.setLetterToPreview(null);
      chatStore.clearPanelViewHistory();
    };

    // The editor's debounced autosave: PATCH the open version (REV-2467).
    const handleEditorSave = async (content: string) => {
      chatStore.noteLetterEdit(content);
      await chatStore.saveLetterEdit();
    };

    /**
     * Download the letter as the backend renders it (REV-2047).
     *
     * The document is built from the stored letter, so any edit still sitting in
     * the autosave debounce has to be committed first — otherwise the file is a
     * version behind and nothing about it looks wrong. If that save cannot be
     * confirmed we refuse: a download silently missing the user's last edit is
     * worse than one that did not happen.
     */
    const handleLetterDownload = async (format: AppealLetterFormat) => {
      const chatId = chatStore.letterToPreview?.chatId;
      if (!chatId) {
        return;
      }

      if (!(await chatStore.saveLetterEdit())) {
        notifier.showError(
          "Could not save your latest changes, so the download was cancelled. Check your connection and try again.",
        );
        return;
      }

      // The version on screen, which may be an older, read-only one.
      const result = await saveAppealLetter(
        chatId,
        format,
        chatStore.chatDetails?.claim_number,
        chatStore.letterToPreview?.generation,
      );

      if (!result.success) {
        notifier.showError(result.error ?? "Failed to download the letter");
      }
    };

    // When chat is linked to a claim, show ClaimPreview
    if (chatStore.claimIdToPreview) {
      return (
        <ClaimPreview
          viewerPosition={panelPosition}
          onTogglePosition={() =>
            setPanelPosition(panelPosition === "left" ? "right" : "left")
          }
          claimId={chatStore.claimIdToPreview}
        />
      );
    }

    return (
      <SidePanelContent
        activePanel={activePanel}
        position={panelPosition}
        editor={{
          messageEditorRef,
          editingContent: chatStore.letterToPreview?.content || "",
          editorPosition: panelPosition,
          onClose: handleClosePanel,
          onBack: () => chatStore.goBackPanelView(),
          canGoBack: chatStore.canGoBackPanelView,
          onTogglePosition: () =>
            setPanelPosition(panelPosition === "left" ? "right" : "left"),
          onSave: handleEditorSave,
          onDownload: handleLetterDownload,
          onChange: (content?: string) => {
            if (content !== undefined) {
              chatStore.noteLetterEdit(content);
            }
          },
          readOnly: chatStore.isLetterPreviewReadOnly,
          versionKey: chatStore.letterToPreview
            ? `${chatStore.letterToPreview.chatId}:${chatStore.letterToPreview.generation}`
            : undefined,
          // The letter itself carries no metadata; the message that produced it does.
          metadata: chatStore.messages.find(
            (message) => message.id === chatStore.letterToPreview?.message,
          )?.metadata,
        }}
        viewer={{
          initialPage: chatStore.fileToPreview?.page,
          rotation: chatStore.fileToPreview?.page_rotation_mapping,
          displayName: chatStore.fileToPreview?.display_name,
          fileName:
            chatStore.fileToPreview?.name ||
            chatStore.fileToPreview?.original_name ||
            "",
          fileContent:
            activeFileRawContent?.fileContent ||
            chatStore.fileToPreview?.file_url ||
            "",
          fileContentType:
            activeFileRawContent?.contentType ||
            chatStore.fileToPreview?.file_type ||
            "",
          rawContent: activeFileRawContent?.rawContent || "",
          fileAttachment: chatStore.fileToPreview,
          viewerPosition: panelPosition,
          onClose: handleClosePanel,
          onBack: () => chatStore.goBackPanelView(),
          canGoBack: chatStore.canGoBackPanelView,
          onTogglePosition: () =>
            setPanelPosition(panelPosition === "left" ? "right" : "left"),
          isLoading: isLoadingFile,
        }}
      />
    );
  },
);

export default ChatPanel;
